/**
 * @file RequirePermission — guard para rutas que requieren un permiso
 * atómico (feature 063). Reemplaza el patrón hardcoded de RequireAdmin
 * cuando una ruta debe ser accesible por un rol custom con un
 * permission slug específico (ej. `dashboard.executive_read`,
 * `audit.read`, `bpm.read`).
 *
 * Default fallback: `superuser` siempre pasa. Si el backend cachea o
 * la sesión no trae `permissions[]` (caso legacy), también pasa para
 * no bloquear superusers con sesiones viejas.
 *
 * Backend RBAC.Authorize sigue siendo SSOT: este guard es UX para
 * evitar pantallas rotas con 403 + redirige a /app de forma temprana.
 */
import { type ReactElement, useEffect, useState } from "react";
import { Navigate } from "react-router";

import { RouteSkeleton } from "../../components/RouteSkeleton";
import { getSession, type SessionUser } from "../../features/login/lib/auth";
import { hasPerm } from "./hasPerm";

interface RequirePermissionProps {
  readonly children: ReactElement;
  /** Permiso atómico requerido (ej. "dashboard.executive_read"). */
  readonly permission: string;
  /** Roles que pasan como fallback cuando `permissions[]` no viene. */
  readonly fallbackRoles?: ReadonlyArray<string>;
}

type State =
  | { status: "checking" }
  | { status: "ok"; user: SessionUser }
  | { status: "forbidden" }
  | { status: "ko" };

// Default estable a nivel de módulo: usar un literal inline `["superuser"]` como
// valor por defecto creaba un array NUEVO en cada render. Como ese array iba en
// las deps del useEffect, el efecto se re-ejecutaba en cada render → setState →
// re-render → efecto otra vez → BUCLE infinito de render/efecto. Eso saturaba
// el main thread y volvía cada interacción (cambio de pestaña) un freeze de
// 14-28s en las rutas que usaban el default (ej. /app/production). Ver perfil
// JS Self-Profiling: storm de reconciliación de React disparado por este loop.
const DEFAULT_FALLBACK_ROLES: ReadonlyArray<string> = ["superuser"];

export function RequirePermission({
  children,
  permission,
  fallbackRoles = DEFAULT_FALLBACK_ROLES,
}: RequirePermissionProps): ReactElement {
  const [state, setState] = useState<State>({ status: "checking" });

  // Clave estable por CONTENIDO (no por identidad del array): así el efecto se
  // ejecuta solo cuando cambia el permiso o el contenido de fallbackRoles, no
  // en cada render. Cubre tanto el default como callers que pasan literales.
  const fallbackKey = fallbackRoles.join(" ");

  useEffect(() => {
    let cancelled = false;
    getSession()
      .then((user) => {
        if (cancelled) return;
        if (!user) {
          setState({ status: "ko" });
          return;
        }
        if (!hasPerm(user, permission, fallbackRoles)) {
          setState({ status: "forbidden" });
          return;
        }
        setState({ status: "ok", user });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "ko" });
      });
    return () => {
      cancelled = true;
    };
    // fallbackKey representa el contenido de fallbackRoles (estable por valor).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission, fallbackKey]);

  if (state.status === "checking") {
    return <RouteSkeleton variant="app" />;
  }
  if (state.status === "ko") {
    return <Navigate to="/login" replace />;
  }
  if (state.status === "forbidden") {
    return <Navigate to="/app" replace />;
  }
  return children;
}
